"use client";

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isAPIError } from "@/api/client";
import { toast } from "@/components/ui/use-toast";

/**
 * Hook for consistent API error handling with toast notifications.
 * Logs errors to console and shows user-friendly toast messages.
 */
export function useApiError() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleError = useCallback((error: unknown, fallbackMessage?: string) => {
    console.error("API Error:", error);

    let title = t("errors.title");
    let description = fallbackMessage || t("errors.defaultMessage");
    let shouldNavigateToPricing = false;

    if (isAPIError(error)) {
      // Use API error message if available, otherwise use fallback
      description = error.message || description;

      // Map error codes to user-friendly titles
      switch (error.code) {
        case "unauthenticated":
          title = t("errors.unauthenticated.title");
          description = t("errors.unauthenticated.message");
          break;
        case "permission_denied":
          title = t("errors.permissionDenied.title");
          description = t("errors.permissionDenied.message");
          break;
        case "not_found":
          title = t("errors.notFound.title");
          break;
        case "invalid_argument":
          title = t("errors.invalidArgument.title");
          break;
        case "resource_exhausted":
          title = t("errors.resourceExhausted.title");
          // Keep the actual error message from backend (quota info)
          // Only use fallback if no message provided
          if (!error.message) {
            description = t("errors.resourceExhausted.message");
          }
          break;
        case "failed_precondition":
          title = t("errors.failedPrecondition.title");
          // Keep the actual error message from backend (subscription info)
          if (!error.message) {
            description = t("errors.failedPrecondition.message");
          }
          shouldNavigateToPricing = true;
          break;
      }
    } else if (error instanceof Error) {
      description = error.message || description;
    }

    toast({
      variant: "destructive",
      title,
      description,
    });

    if (shouldNavigateToPricing) {
      // Small delay to let the user read the toast
      setTimeout(() => {
        navigate("/?section=pricing");
      }, 1500);
    }
  }, [navigate, t]);

  return { handleError };
}

/**
 * Wraps an async operation with error handling.
 * Returns a tuple of [result, error] similar to Go-style error handling.
 */
export async function withApiError<T>(
  operation: () => Promise<T>,
  handleError: (error: unknown, fallbackMessage?: string) => void,
  fallbackMessage?: string
): Promise<[T | null, unknown]> {
  try {
    const result = await operation();
    return [result, null];
  } catch (error) {
    handleError(error, fallbackMessage);
    return [null, error];
  }
}
